import { useCallback, useRef, useState } from 'react';

/**
 * Drag-to-reorder for an automation's step list.
 *
 * Native HTML5 drag rather than a library: the DnD email editor already reorders
 * its layers this way (see components/dnd-editor-v2/DndCanvas), so this matches
 * the house pattern and adds no dependency.
 *
 * The list is reordered locally the moment the drag ends and the new order is
 * sent to the server as one bulk call. On failure the caller restores the order
 * it captured before the move — a step list is short and the whole sequence is
 * rewritten server-side, so an optimistic move with a hard revert is simpler and
 * less surprising than leaving the cards mid-flight while a request is pending.
 */

/** Payload type shared with the drop indicator: null = no active drag. */
export interface StepDragState {
  dragIndex: number | null;
  /** Insertion slot, 0..length. The card is dropped BEFORE this index. */
  dropIndex: number | null;
}

interface UseStepDragReorderOptions<T> {
  items: T[];
  /** Commit the new order. Reject to have the previous order restored. */
  onReorder: (nextItems: T[], previous: T[]) => void;
  /** Blocks dragging while a step is still being created, etc. */
  disabled?: boolean;
}

export function useStepDragReorder<T>({ items, onReorder, disabled }: UseStepDragReorderOptions<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  // The pointer can leave and re-enter child elements of the same card, firing
  // dragleave after dragenter. Counting depth avoids the indicator flickering.
  const enterDepth = useRef(0);

  const reset = useCallback(() => {
    setDragIndex(null);
    setDropIndex(null);
    enterDepth.current = 0;
  }, []);

  /**
   * `draggable` sits on the grip handle only, matching the DnD editor, so the
   * card keeps its own click targets and text selection. `ghostEl` is the card
   * itself — without it the drag image would be just the little grip icon.
   */
  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number, ghostEl?: HTMLElement | null) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      setDragIndex(index);
      e.dataTransfer.effectAllowed = 'move';
      // Some browsers cancel a drag with no payload set.
      e.dataTransfer.setData('text/plain', `step-${index}`);

      if (ghostEl) {
        const rect = ghostEl.getBoundingClientRect();
        e.dataTransfer.setDragImage(ghostEl, e.clientX - rect.left, e.clientY - rect.top);
      }
    },
    [disabled],
  );

  /**
   * Whether the pointer is in the top or bottom half of the hovered card decides
   * which side of it the dragged card lands on, so a one-position move works
   * without having to aim at a thin gap.
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      if (dragIndex === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      const rect = e.currentTarget.getBoundingClientRect();
      const after = e.clientY - rect.top > rect.height / 2;
      setDropIndex(after ? index + 1 : index);
    },
    [dragIndex],
  );

  const handleDragEnter = useCallback(() => {
    enterDepth.current += 1;
  }, []);

  const handleDragLeave = useCallback(() => {
    enterDepth.current -= 1;
    if (enterDepth.current <= 0) setDropIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (dragIndex === null || dropIndex === null) {
        reset();
        return;
      }

      // Dropping just above or just below where the card already sits is a no-op,
      // not a move — don't spend a request on it.
      if (dropIndex === dragIndex || dropIndex === dragIndex + 1) {
        reset();
        return;
      }

      const previous = items;
      const next = [...items];
      const [moved] = next.splice(dragIndex, 1);
      // Removing the card first shifts every later slot down by one.
      const target = dropIndex > dragIndex ? dropIndex - 1 : dropIndex;
      next.splice(target, 0, moved);

      reset();
      onReorder(next, previous);
    },
    [dragIndex, dropIndex, items, onReorder, reset],
  );

  return {
    dragIndex,
    dropIndex,
    isDragging: dragIndex !== null,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragEnd: reset,
  };
}
